import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Wrench, Eye, FileUp, CheckCircle2, MessageSquare, Gavel, Activity,
} from "lucide-react";
import type { StageKey } from "@/lib/lrDirect/deriveStages";

export type CTARole = "shipper" | "broker";

interface Props {
  currentStage: StageKey | null;
  role: CTARole;
  loadId: string;
  hasDispatch: boolean;
  hasPod: boolean;
  onMarkComplete?: () => void;
  onUpdateStatus?: () => void;
  onUploadPod?: () => void;
  onMessageBroker?: () => void;
}

interface Action {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  onClick?: () => void;
  variant?: "default" | "outline";
}

function actionsFor(props: Props): Action[] {
  const { currentStage, role, loadId, hasDispatch, hasPod, onMarkComplete, onUpdateStatus, onUploadPod, onMessageBroker } = props;
  if (!currentStage || currentStage === "closed") return [];

  const detail = (tab?: string) => `/lr-direct/load/${loadId}${tab ? `?tab=${tab}` : ""}`;
  const builder = `/lr-direct/dispatches/build/${loadId}`;
  // The bid room is the shipper's bid/award surface (owner-only route).
  const bidRoom = `/lr-direct/live/${loadId}`;

  if (currentStage === "posted") {
    // Pre-award: shippers can front-load dispatch prep (stops, contacts,
    // instructions) while bids are still coming in. The winning broker is
    // auto-attached on award via trg_direct_dispatch_autoset_broker + the
    // award RPC upsert — brokerless drafts fire no broker notifications.
    return role === "shipper"
      ? [
          { label: "View bids", icon: Gavel, to: bidRoom },
          { label: hasDispatch ? "Edit Dispatch" : "Prepare Dispatch", icon: Wrench, to: builder, variant: "outline" },
        ]
      : [{ label: "Place bid", icon: Gavel, to: detail("bids") }];
  }
  if (currentStage === "awarded") {
    return [{ label: hasDispatch ? "Edit Dispatch" : "Build Dispatch", icon: Wrench, to: builder }];
  }
  if (currentStage === "dispatch_created" || currentStage === "broker_accepted") {
    return role === "shipper"
      ? [{ label: "View Dispatch", icon: Eye, to: detail("dispatch") }]
      : [{ label: "Edit Dispatch", icon: Wrench, to: builder }];
  }
  if (
    currentStage === "truck_assigned" ||
    currentStage === "pickup_scheduled" ||
    currentStage === "picked_up" ||
    currentStage === "in_transit"
  ) {
    if (role === "broker") {
      return [
        { label: "Update Status", icon: Activity, onClick: onUpdateStatus ?? (() => {}) },
        { label: "Open Dispatch", icon: Wrench, to: builder, variant: "outline" },
      ];
    }
    const shipperActions: Action[] = [
      { label: "View Status", icon: Activity, to: detail("timeline") },
    ];
    if (onMessageBroker) {
      shipperActions.push({ label: "Message Broker", icon: MessageSquare, onClick: onMessageBroker, variant: "outline" });
    }
    return shipperActions;
  }
  if (currentStage === "delivered") {
    if (role === "broker") {
      return hasPod
        ? [{ label: "Mark Complete", icon: CheckCircle2, onClick: onMarkComplete ?? (() => {}) }]
        : [{ label: "Upload POD", icon: FileUp, onClick: onUploadPod ?? (() => {}), to: onUploadPod ? undefined : detail("documents") }];
    }
    return hasPod
      ? [{ label: "View POD", icon: Eye, to: detail("documents") }]
      : [{ label: "View Documents", icon: Eye, to: detail("documents") }];
  }
  if (currentStage === "pod_uploaded") {
    return role === "broker"
      ? [{ label: "Mark Complete", icon: CheckCircle2, onClick: onMarkComplete ?? (() => {}) }]
      : [{ label: "View POD", icon: Eye, to: detail("documents") }];
  }
  return [];
}

export function LoadStageCTABar(props: Props) {
  const navigate = useNavigate();
  const actions = actionsFor(props);
  if (actions.length === 0) return null;

  return (
    <div className="border border-border rounded-lg bg-card px-4 py-3 mb-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="text-xs text-muted-foreground">
        <span className="font-semibold text-foreground uppercase tracking-wide">Next step</span>
        <span className="ml-2 capitalize">
          {String(props.currentStage ?? "").replace(/_/g, " ")}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {actions.map((a, i) => {
          const Icon = a.icon;
          const variant = a.variant ?? (i === 0 ? "default" : "outline");
          const cls = variant === "default"
            ? "bg-brand-teal hover:bg-brand-teal/90 text-white gap-1.5 h-8 text-xs"
            : "gap-1.5 h-8 text-xs";
          return (
            <Button
              key={a.label}
              size="sm"
              variant={variant === "default" ? "default" : "outline"}
              className={cls}
              onClick={() => {
                if (a.onClick) a.onClick();
                else if (a.to) navigate(a.to);
              }}
            >
              <Icon className="h-3.5 w-3.5" />
              {a.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
